<script setup>
import TransitionFadeIn from '@/components/shared/transitions/TransitionFadeIn.vue'
import {useFloating, autoUpdate, flip, shift, offset as offsetFn, arrow} from '@floating-ui/vue'
import {useDebounceFn} from '@vueuse/core'
import {ref, toRefs, computed} from 'vue'

//Props
const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  position: {
    type: String,
    default: 'bottom',
  },
  offset: {
    type: Number,
    default: 10,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isCompact: {
    type: Boolean,
    default: false,
  },
  showDelay: {
    type: Number,
    default: 0,
  },
  hideDelay: {
    type: Number,
    default: 0,
  },
  noWrap: {
    type: Boolean,
    default: false,
  },
})

//Get refs
const {showDelay, hideDelay, offset} = toRefs(props)
const placement = ref(props.position) //Can't be the prop ref directly

//Element refs
const trigger = ref(null)
const floating = ref(null)
const floatingArrow = ref(null)
const isHidden = ref(true)

//Use floating UI
const {
  floatingStyles,
  middlewareData,
} = useFloating(trigger, floating, {
  placement,
  middleware: [
    offsetFn(offset.value),
    flip(),
    shift(),
    arrow({element: floatingArrow}),
  ],
  whileElementsMounted: autoUpdate,
})

//Arrow style
const arrowStyles = computed(() => {
  const placement = middlewareData.value.offset?.placement
  const x = middlewareData.value.arrow?.x
  return {
    left: x !== null ? `${x}px` : '',
    top: placement !== 'top' ? '-5px' : '',
  }
})

//Track if mouse is over the element
const isMouseOver = ref(false)

//Debounced show/hide handlers
//These track if mouse is still over the element or not to prevent odd behaviour
const show = useDebounceFn(() => {
  if (isMouseOver.value) {
    isHidden.value = false
  }
}, showDelay.value)
const hide = useDebounceFn(() => {
  if (!isMouseOver.value) {
    isHidden.value = true
  }
}, hideDelay.value)

//Mouse enter/leave handlers
const mouseEnter = () => {
  isMouseOver.value = true
  show()
}
const mouseLeave = () => {
  isMouseOver.value = false
  hide()
}
</script>

<template>
  <span :class="$style.wrapper">
    <span
      ref="trigger"
      :class="$style.trigger"
      is-clickable
      @mouseenter="mouseEnter()"
      @mouseleave="mouseLeave()"
    >
      <slot />
    </span>
    <TransitionFadeIn>
      <div
        v-if="!isHidden && !isDisabled"
        ref="floating"
        role="floating"
        :class="{
          [$style.tooltip]: true,
          [$style.compact]: isCompact,
          [$style[placement]]: true,
          [$style.noWrap]: noWrap,
        }"
        :style="floatingStyles"
      >
        <span>{{ text }}</span>
        <div
          ref="floatingArrow"
          :class="$style.arrow"
          :style="arrowStyles"
        />
      </div>
    </TransitionFadeIn>
  </span>
</template>

<style module lang="scss">
.wrapper {
  /* Needed to avoid extra whitespace causing padding. With Vue 3, we shouldn't need this wrapper element anymore */
  font-size: 0;
}
.trigger {
  display: inline-block;
}
.tooltip {
  position: absolute;
  text-align: center;
  font-size: var(--font-size-text-normal);
  line-height: 18px;
  padding: 0.25rem 0.5rem;
  color: var(--color-text);
  background: var(--bg-interactive-element-hover);
  border: 0;
  border-radius: var(--border-radius-m);
  box-shadow: var(--box-shadow);
  z-index: 99999;
  &.compact {
    max-width: 150px;
  }
  &.top {
    margin-bottom: 2.5rem
  }
  &.bottom {
    margin-top: 1.5rem
  }
  &.noWrap {
    white-space: nowrap;
  }
}
.arrow {
  position: absolute;
  background: var(--bg-interactive-element-hover);
  width: 8px;
  height: 8px;
  transform: rotate(45deg);
}
</style>
